import axios from "axios";
import {AccountApiFailureKind, classifyAccountApiFailure} from "account/accountApiFailure";
import type {SupportMessages} from "../i18n/en/support";

/**
 * What to tell the user when `POST /support/request` does not succeed
 * (kitecorp/transferify-api-service#101). React-free by mandate (epic #218), and the same
 * status-to-kind shape `download/downloadErrors.ts` uses for the download surface.
 *
 * Every status the api documents gets its OWN kind, because the remedies genuinely differ: log back
 * in, wait, reword, or email us directly. Collapsing them into one "please try again" would give
 * three of the four users advice that cannot work — which is precisely the dead end ui#326 removed
 * from the feedback form.
 */

/**
 * The failure kinds, derived from the `support.failures` catalog namespace so a kind can never exist
 * without copy to render it (and vice versa) — a mismatch is a compile error rather than a blank spot
 * in the dialog.
 */
export type SupportRequestFailure = keyof SupportMessages["support"]["failures"];

/** Classifies a rejected send. Anything unrecognised is `unknown` rather than a confident wrong guess. */
export function supportRequestFailureOf(error: unknown): SupportRequestFailure {
	const status = axios.isAxiosError(error) ? error.response?.status : undefined;
	switch (status) {
		case 400:
			// The client mirror and the api disagreed about what is valid. Telling the user to "try
			// again" would send them back into text the server has already refused.
			return "rejected";
		case 401:
			return "notSignedIn";
		case 429:
			return "rateLimited";
		case 502:
			return "undeliverable";
		default:
			return "unknown";
	}
}

/**
 * The api's OWN sentence about a rejected send, or `null` when it gave none.
 *
 * Worth preferring over our copy for two reasons (ui#2261). It is already in the caller's language —
 * `Accept-Language` rides every request (ui#125) — and its delivery-failed message names the support
 * inbox of the BRAND the request came from (api#1487), which is a fact about the routing the ui is
 * describing rather than one the ui decides.
 *
 * The "did the server actually say something usable" narrowing is `classifyAccountApiFailure`'s
 * rather than a second copy of it: it is the same boundary question about the same error envelope,
 * and two versions of it would disagree the first time one of them learned about a new empty body.
 */
export function supportRequestMessageOf(error: unknown): string | null {
	const response = axios.isAxiosError(error) ? error.response : undefined;
	const message = (response?.data as {message?: unknown} | undefined)?.message;
	const explained = classifyAccountApiFailure({status: response?.status ?? null, message})
		=== AccountApiFailureKind.REJECTED;
	return explained && typeof message === "string" ? message : null;
}

/**
 * The sentence to show for a failed send: the api's own explanation when it gave one, and the
 * catalog's copy for the classified kind otherwise.
 *
 * @param fallbacks the `failures` namespace of whichever dialog is asking — the support dialog and
 *   the withdrawal dialog word the same five outcomes differently, so the copy is passed in and this
 *   stays React-free.
 */
export function supportRequestFailureText(
	failure: SupportRequestFailure,
	apiMessage: string | null,
	fallbacks: Record<SupportRequestFailure, string>,
): string {
	return apiMessage ?? fallbacks[failure];
}

/**
 * Whether to offer the direct `mailto:` escape hatch.
 *
 * True for exactly one failure: an undeliverable (502) request was accepted and understood, but the
 * support mail could not leave our side — so resubmitting the same form reaches the same broken
 * transport, and the ONLY route left to the user is writing to us directly. Offering the address for a
 * rate limit or an expired session would instead teach people to bypass a channel that works.
 */
export function showsMailtoFallback(failure: SupportRequestFailure): boolean {
	return failure === "undeliverable";
}
